import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { apiJson } from '@/lib/api';
import { track } from '@/lib/analytics';
import type { SelectedConversation, UserResponse } from '@/lib/types';
import { ChatView } from '@/components';

type ChatRoomResponse = {
  id: number;
  title: string;
  creator_user_id: number;
  is_archived: boolean;
};

function CustomChatPage() {
  const { chatId } = Route.useParams();
  const id = parseInt(String(chatId), 10);
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();

  const userQuery = useQuery({
    queryKey: ['user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/user'),
    enabled: isLoaded && !!isSignedIn,
  });

  const roomQuery = useQuery({
    queryKey: ['chat-room', id],
    queryFn: () => apiJson<ChatRoomResponse>(getToken, `/v1/chats/${id}`),
    enabled: isLoaded && !!isSignedIn && Number.isFinite(id) && id > 0,
  });

  useEffect(() => {
    track('Custom Chat Screen', { chat_id: id });
  }, [id]);

  const handleBack = () => {
    navigate({ to: '/app/chat' });
  };

  const conv: SelectedConversation = {
    type: 'custom-chat',
    chatId: id,
    title: roomQuery.data?.title ?? 'Chat',
    isArchived: roomQuery.data?.is_archived ?? false,
    isCreator:
      userQuery.data?.id != null && roomQuery.data
        ? userQuery.data.id === roomQuery.data.creator_user_id
        : false,
  };

  if (!isLoaded || !isSignedIn) {
    return null;
  }

  if (roomQuery.isError) {
    return (
      <div className='min-h-screen flex flex-col items-center justify-center bg-sky-light p-4'>
        <p className='text-gray-600 text-center'>This chat is not available.</p>
        <button
          type='button'
          onClick={handleBack}
          className='mt-4 text-primary font-semibold'
        >
          Back
        </button>
      </div>
    );
  }

  return (
    <div className='flex h-dvh flex-col overflow-hidden bg-sky-light'>
      <ChatView
        conversation={conv}
        currentUser={userQuery.data}
        onBack={handleBack}
        showHeader={true}
      />
    </div>
  );
}

export const Route = createFileRoute('/app/chat/custom/$chatId')({
  component: CustomChatPage,
});
